import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useMaintenancePlanDetail, useMaintenancePlanChecklists, useMaintenancePlanResources, useMaintenancePlan } from './hooks/useMaintenancePlan';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table } from '@/components/ui';
import { MaintenancePlanFormModal } from './components/MaintenancePlanFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  pending_approval: 'bg-amber-100 text-amber-600',
  active: 'bg-emerald-100 text-emerald-600',
  paused: 'bg-indigo-100 text-indigo-600',
  expired: 'bg-red-100 text-red-600',
  archived: 'bg-slate-200 text-slate-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  pending_approval: 'Chờ duyệt',
  active: 'Đang áp dụng',
  paused: 'Tạm dừng',
  expired: 'Hết hạn',
  archived: 'Lưu trữ',
};

const priorityColors: Record<string, string> = {
  low: 'bg-slate-100 text-slate-500',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-amber-100 text-amber-600',
  critical: 'bg-red-100 text-red-600',
};

export default function MaintenancePlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: item, isLoading } = useMaintenancePlanDetail(id);
  const { data: checklists = [], isLoading: isLoadingCheck } = useMaintenancePlanChecklists(id);
  const { data: resources = [], isLoading: isLoadingRes } = useMaintenancePlanResources(id);
  const { updatePlan } = useMaintenancePlan(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết kế hoạch...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu kế hoạch</div>;

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'scope', label: 'Phạm vi & Đối tượng' },
    { value: 'checklist', label: 'Checklist QA' },
    { value: 'resources', label: 'Tài nguyên' },
    { value: 'schedules', label: 'Lịch đã phát sinh' },
    { value: 'history', label: 'Phiên bản & Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={item.name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">{item.code}</span>
          <span className="text-slate-300">|</span>
          <Badge className={`${priorityColors[item.priority_code]} border-transparent text-[10px] font-bold `}>
            {item.priority_code}
          </Badge>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1 ">
            <i className="ri-calendar-line" />
            {item.frequency_type} (Mỗi {item.frequency_interval} kỳ)
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Kế hoạch bảo dưỡng', path: '/maintenance/plan' },
        { label: item.name }
      ]}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-copy-line">Sao chép (Clone)</Button>
          <AuthButton module="maintenance" action="generate_schedule" variant="primary" icon="ri-magic-line">
            Phát sinh lịch trình
          </AuthButton>
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Giờ dự tính" value={item.estimated_hours} icon="ri-time-line" variant="info" suffix="h" />
                <KPICard title="Nhân sự chuẩn" value={item.standard_staff_count} icon="ri-team-line" variant="success" suffix="người" />
                <KPICard title="Tần suất" value={item.frequency_type} icon="ri-repeat-line" variant="neutral" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả & Lưu ý kế hoạch
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  {item.notes || 'Chưa có ghi chú chi tiết cho kế hoạch này.'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-6">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án quản lý</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Hợp đồng liên kết</span>
                    <span className="text-sm font-bold text-os-primary">{item.contract?.contract_number || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày hiệu lực</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(item.effective_from).toLocaleDateString('vi-VN')}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ngày kết thúc</span>
                    <span className="text-sm font-bold text-slate-700">{item.effective_to ? new Date(item.effective_to).toLocaleDateString('vi-VN') : 'Vô thời hạn'}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <i className="ri-list-check-line text-os-primary" />
                    Checklist QA vận hành
                  </h3>
                  <Button variant="ghost" size="sm" className="text-os-primary text-xs font-bold" onClick={() => setActiveTab('checklist')}>
                    Quản lý checklist
                  </Button>
                </div>
                <div className="space-y-3">
                  {checklists.slice(0, 5).map(check => (
                    <div key={check.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all border border-transparent hover:border-slate-200">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-slate-400">#{check.sequence_order}</span>
                        <span className="text-sm font-bold text-slate-800">{check.task_name}</span>
                        {check.is_mandatory && <Badge variant="neutral" className="bg-red-50 text-red-600 text-[9px] ">Bắt buộc</Badge>}
                      </div>
                      <i className="ri-arrow-right-s-line text-slate-300" />
                    </div>
                  ))}
                  {checklists.length === 0 && <span className="text-sm text-slate-400 italic">Chưa có checklist</span>}
                </div>
              </Card>
            </>
          )}

          {activeTab === 'checklist' && (
            <Card className="overflow-hidden border-none shadow-sm">
              <Table
                columns={[
                  { key: 'seq', title: 'TT', align: 'center' },
                  { key: 'task', title: 'Nội dung kiểm tra' },
                  { key: 'standard', title: 'Tiêu chuẩn QA' },
                  { key: 'mandatory', title: 'Bắt buộc', align: 'center' },
                ]}
                data={checklists}
                isLoading={isLoadingCheck}
                renderCell={(check, key) => {
                  switch (key) {
                    case 'seq': return <span className="font-bold text-slate-400">{check.sequence_order}</span>;
                    case 'task': return <span className="text-sm font-bold text-slate-800">{check.task_name}</span>;
                    case 'standard': return <span className="text-xs text-slate-500 italic">{check.quality_standard || '---'}</span>;
                    case 'mandatory': return check.is_mandatory ? <i className="ri-checkbox-circle-fill text-red-500" /> : <i className="ri-checkbox-blank-circle-line text-slate-200" />;
                    default: return null;
                  }
                }}
              />
            </Card>
          )}

          {activeTab === 'resources' && (
            <Card className="overflow-hidden border-none shadow-sm">
              <Table
                columns={[
                  { key: 'type', title: 'Loại tài nguyên' },
                  { key: 'id', title: 'Định danh' },
                  { key: 'qty', title: 'Số lượng / Định mức', align: 'right' },
                ]}
                data={resources}
                isLoading={isLoadingRes}
                renderCell={(res, key) => {
                  switch (key) {
                    case 'type': return (
                      <Badge variant="neutral" className=" text-[10px]">
                        {res.resource_type === 'human' ? 'Nhân sự' : res.resource_type === 'equipment' ? 'Thiết bị' : 'Vật tư'}
                      </Badge>
                    );
                    case 'id': return <span className="text-sm font-bold text-slate-800">{res.resource_id || 'Chung'}</span>;
                    case 'qty': return <span className="font-black text-os-primary">{res.quantity} <span className="text-[10px] text-slate-400 ">{res.unit || '---'}</span></span>;
                    default: return null;
                  }
                }}
              />
            </Card>
          )}

          {(activeTab === 'scope' || activeTab === 'schedules' || activeTab === 'history') && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Phần này đang được tổng hợp dữ liệu từ quy trình lập kế hoạch chuyên sâu."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-focus-3-line text-os-primary" />
              Phạm vi áp dụng
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Loại phạm vi</span>
                <span className="font-bold  tracking-wider">{item.target_scope_type}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Khu vực (Zone)</span>
                <span className="font-bold text-os-primary">{item.zone?.zone_name || 'Toàn dự án'}</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                <p className="text-[10px] text-slate-500 font-bold  mb-2">Đối tượng cụ thể</p>
                <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/50 text-[11px] text-slate-300">
                  {typeof item.target_scope_value === 'string' ? item.target_scope_value : 'Tất cả tài sản thuộc loại hình công việc'}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-repeat-line text-slate-400" />
              Tần suất & Chu kỳ
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500  font-bold">Chu kỳ lập lịch</span>
                <span className="font-bold text-os-primary">{item.frequency_type}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-500">Khoảng cách</span>
                <span className="font-bold text-slate-700">{item.frequency_interval} {item.frequency_type === 'daily' ? 'Ngày' : item.frequency_type === 'weekly' ? 'Tuần' : 'Tháng'}</span>
              </div>
              <Alert variant="info" icon="ri-information-line">
                Kế hoạch này sẽ tự động phát sinh lịch trình vào ngày 25 hàng tháng.
              </Alert>
            </div>
          </Card>
        </div>
      </div>

      <MaintenancePlanFormModal
        isOpen={showEdit}
        item={item}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
